import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Paperclip,
  Mic,
  Square,
  X,
  FileText,
  CornerDownLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (message: string, files?: File[]) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  placeholder?: string;
  maxChars?: number;
  disabled?: boolean;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  onStop,
  isStreaming = false,
  placeholder = "Ask about KRA compliance, eTIMS, tax returns, payroll…",
  maxChars = 4000,
  disabled = false,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 180) + "px";
  }, [value]);

  const handleSubmit = useCallback(() => {
    if (!value.trim() || isStreaming || disabled) return;
    onSend(value.trim(), attachedFiles.length ? attachedFiles : undefined);
    setAttachedFiles([]);
  }, [value, isStreaming, disabled, attachedFiles, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setAttachedFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
      e.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const charCount = value.length;
  const nearLimit = charCount > maxChars * 0.875;
  const canSend = value.trim().length > 0 && !isStreaming && !disabled;

  return (
    <div
      className="border-t border-[var(--color-border)] bg-white px-3 sm:px-4 py-3 relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-[#FAF0F2] border-2 border-dashed border-[var(--color-burgundy)] rounded-[var(--radius-lg)] flex items-center justify-center"
          >
            <div className="text-center pointer-events-none">
              <Paperclip
                size={22}
                className="text-[var(--color-burgundy)] mx-auto mb-2"
              />
              <p className="text-[13px] font-semibold text-[var(--color-burgundy)]">
                Drop to attach
              </p>
              <p className="text-[11px] ">PDF, images, documents</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {attachedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="flex flex-wrap gap-1.5 mb-2"
          >
            {attachedFiles.map((file, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 bg-[#FAF0F2] border border-[var(--color-burgundy)]/20 rounded-full px-2.5 py-1 text-[10.5px] font-medium text-[var(--color-burgundy)]"
              >
                <FileText size={10} className="flex-shrink-0" />
                <span className="max-w-[110px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="hover:opacity-70 transition-opacity"
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={cn(
          "flex flex-col bg-[var(--color-surface)] border rounded-[var(--radius-lg)] transition-all",
          isDragging
            ? "border-[var(--color-burgundy)] bg-[#FAF0F2]"
            : "border-[var(--color-border)] focus-within:border-[var(--color-burgundy)] focus-within:shadow-[0_0_0_3px_rgba(107,30,42,0.08)]",
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isStreaming || disabled}
          rows={1}
          aria-label="Chat message"
          className="w-full bg-transparent px-4 pt-3 pb-1 text-[13.5px] text-[var(--color-charcoal)] placeholder: resize-none focus:outline-none leading-relaxed disabled:opacity-50"
          style={{ minHeight: 44, maxHeight: 180 }}
        />

        <div className="flex items-center justify-between px-3 pb-2.5 pt-1">
          <div className="flex items-center gap-0.5">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7  hover:text-[var(--color-charcoal)]"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach file"
                  >
                    <Paperclip size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Attach file</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 transition-colors",
                      isRecording
                        ? "text-red-500 bg-red-50 hover:bg-red-100 animate-pulse"
                        : " hover:text-[var(--color-charcoal)]",
                    )}
                    onClick={() => setIsRecording((r) => !r)}
                    aria-label={isRecording ? "Stop recording" : "Voice input"}
                  >
                    <Mic size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isRecording ? "Stop recording" : "Voice input"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {nearLimit && (
              <span className="text-[10px] text-amber-500 font-medium ml-1.5">
                {charCount}/{maxChars}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {canSend && (
              <span className="hidden sm:flex items-center gap-0.5 text-[10px] ">
                <CornerDownLeft size={10} />
                to send
              </span>
            )}

            <Separator orientation="vertical" className="h-4 hidden sm:block" />

            {isStreaming ? (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-3 text-[11.5px] gap-1.5 border-[var(--color-border)] "
                onClick={onStop}
              >
                <Square size={10} className="fill-current" />
                Stop
              </Button>
            ) : (
              <motion.div
                whileHover={canSend ? { scale: 1.04 } : {}}
                whileTap={canSend ? { scale: 0.95 } : {}}
              >
                <Button
                  size="sm"
                  disabled={!canSend}
                  onClick={handleSubmit}
                  className={cn(
                    "h-7 px-3 text-[12px] gap-1.5 font-semibold transition-all",
                    canSend
                      ? "gradient-burgundy text-white shadow-sm hover:opacity-90"
                      : "bg-[var(--color-border)]  cursor-not-allowed",
                  )}
                >
                  <Send size={11} />
                  Send
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <p className="text-center text-[10px]  mt-2">
        Rosewood AI · General guidance only · Verify with a certified CPA for
        complex matters
      </p>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileInput}
        accept=".pdf,.doc,.docx,.xlsx,.jpg,.png"
        aria-hidden
      />
    </div>
  );
}
